import { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Box, Button, Grid } from '@material-ui/core';
import * as yup from 'yup';
import { AdviceDelivery, AdviceStatuses, AdviceType, IncomeGeneratorApplicationDto, KiwiSaverApplicationDto, OnboardDelivery, Product, WealthBuilderApplicationDto } from '../../../../api/apiClients';
import { Client, ScreenProps } from '../../../../common/types';
import FormikWrapper from '../../../containers/FormikWrapper';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { FormLabel, HelpTooltip, SubtitleText } from '../../../controls/helpers';
import { ButtonOptionList, Select } from '../../../controls/inputs';
import { clearClient, saveClient } from '../../../../store/adviser';
import { getRoles, hasSelectedProductRoles } from '../../Helpers';

const cleanValues = (values: Client) => {
    let kiwiSaverApplication: KiwiSaverApplicationDto | undefined = undefined;
    let wealthBuilderApplications: WealthBuilderApplicationDto[] = [];
    let incomeGeneratorApplications: IncomeGeneratorApplicationDto[] = [];

    if (values.products?.includes(Product.KiwiSaver))
        kiwiSaverApplication = !values.kiwiSaverApplication || !values.prepopulated ? { active: true } : { ...values.kiwiSaverApplication, active: true };
    if (values.products?.includes(Product.WealthBuilder))
        wealthBuilderApplications = !values.wealthBuilderApplications?.length || !values.prepopulated ? [{ active: true }] : [...values.wealthBuilderApplications.map(a => ({ ...a, active: true }))];
    if (values.products?.includes(Product.IncomeGenerator))
        incomeGeneratorApplications = !values.incomeGeneratorApplications?.length || !values.prepopulated ? [{ active: true }] : [...values.incomeGeneratorApplications.map(a => ({ ...a, active: true }))];

    return {
        ...values,
        kiwiSaverApplication,
        wealthBuilderApplications,
        incomeGeneratorApplications,
        onboardDelivery: (values.products?.length === 1 && values.products.includes(Product.KiwiSaver)) ? OnboardDelivery.BrowserKiwiSaverOnly : (!values.onboardDelivery || values.onboardDelivery === OnboardDelivery.BrowserKiwiSaverOnly) ? OnboardDelivery.Browser : values.onboardDelivery
    };
}

export default ({ nextRoute }: RouteComponentProps & ScreenProps) => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector(state => state.oidc);
    const { client } = useAppSelector(state => state.adviser);
    const [productRoles, setProductRoles] = useState<Product[]>([]);

    useEffect(() => {
        if (user) {
            const roles = getRoles(user);

            setProductRoles([
                ...roles.includes('KiwiSaver') || roles.includes("KiwiSaver Support") ? [Product.KiwiSaver] : [],
                ...roles.includes('WealthBuilder') || roles.includes("WealthBuilder Support") ? [Product.WealthBuilder] : [],
                ...roles.includes('IncomeGenerator') || roles.includes("IncomeGenerator Support") ? [Product.IncomeGenerator] : []
            ]);
        }
    }, [user]);

    return <Box my={2}>
        <FormikWrapper
            enableReinitialize
            initialValues={{
                adviceDelivery: AdviceDelivery.None,
                adviceStatus: AdviceStatuses.Skipped,
                adviceType: AdviceType.Limited,
                products: [] as Product[],
                ...client
            }}
            validationSchema={yup.object({
                products: yup.array().of(yup.mixed<Product>()),
                adviceDelivery: yup.mixed<AdviceDelivery>().when('adviceStatus', {
                    is: AdviceStatuses.None,
                    then: yup.mixed<AdviceDelivery>().required('An option is required')
                })
            })}
            onSubmit={values => {
                const _values = cleanValues(values);
                dispatch(saveClient(_values));

                if (values.adviceDelivery !== AdviceDelivery.InPerson) {
                    nextRoute({ ..._values });
                } else {
                    const queryString = [
                        ...values.products?.includes(Product.KiwiSaver) ? ['ks'] : [],
                        ...values.products?.includes(Product.WealthBuilder) ? ['wb'] : [],
                        ...values.products?.includes(Product.IncomeGenerator) ? ['ig'] : [],
                    ].join('-');
                    dispatch(clearClient());
                    window.open(`/adviser/new-sat-application/${queryString}`, '_blank');
                }
            }}>

            {({ setFieldValue, values }) => <>
                <Box px={6}>
                    <Grid container spacing={3} justifyContent="center" alignItems="center">
                        <Grid item xs={12} style={{ textAlign: 'center' }}>
                            <SubtitleText hideMarginBottom><b>Please select which products to include in the application.</b></SubtitleText>
                        </Grid>
                        <Grid item md={3} xs={12} />
                        <Grid item md={6} xs={12}>
                            <Grid container spacing={3}>
                                <ButtonOptionList
                                    name="products"
                                    values={values.products}
                                    setFieldValue={setFieldValue}
                                    options={[
                                        { label: 'KiwiSaver', value: Product.KiwiSaver, disabled: !productRoles.includes(Product.KiwiSaver) },
                                        { label: 'Wealth Builder', value: Product.WealthBuilder, disabled: !productRoles.includes(Product.WealthBuilder) },
                                        { label: 'Income Generator', value: Product.IncomeGenerator, disabled: !productRoles.includes(Product.IncomeGenerator) }
                                    ]}
                                    onChange={(value, _, checked) => {
                                        if (value === Product.KiwiSaver && !checked) {
                                            setFieldValue('adviceStatus', AdviceStatuses.Skipped);
                                            setFieldValue('adviceDelivery', AdviceDelivery.None);
                                        }
                                    }}
                                />
                                {values.products?.includes(Product.KiwiSaver) &&
                                    <Grid item xs={12}>
                                        <FormLabel label={<>Would you like to start with the Savings Advice Tool? {values.products.includes(Product.IncomeGenerator) && <HelpTooltip title="The Savings Advice Tool provides recommendations for KiwiSaver and Wealth Builder only. Income Generator will be pre-selected for the client when they start onboarding." />}</>} />
                                        <Select
                                            name="adviceDelivery"
                                            options={[
                                                { label: 'No', value: AdviceDelivery.None },
                                                ...hasSelectedProductRoles(user, values?.products) ? [{ label: 'Yes, open in my browser', value: AdviceDelivery.InPerson }] : [],
                                                { label: 'Yes, send link to client', value: AdviceDelivery.ClientSelfDriven }
                                            ]}

                                            onChange={adviceDelivery => {
                                                setFieldValue('adviceType', AdviceType.Limited);
                                                setFieldValue('adviceStatus', adviceDelivery === AdviceDelivery.InPerson || adviceDelivery === AdviceDelivery.ClientSelfDriven ? AdviceStatuses.None : AdviceStatuses.Skipped);
                                            }}
                                        />
                                    </Grid>
                                }
                            </Grid>
                        </Grid>
                        <Grid item md={3} xs={12} />
                        <Grid item md={10} sm={8} xs={7} />
                        <Grid item md={6} xs={12}>
                            <Grid container justifyContent="flex-end">
                                <Grid item>
                                    <Button variant="contained" color="primary" type="submit" disabled={!values.products?.length}>Next</Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            </>}
        </FormikWrapper>
    </Box>
} 
